import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Label,
  Select,
  Spinner,
} from "@fluentui/react-components";
import { authClient } from "../../utils/auth-client";
import { apiUrl } from "../../utils/api";
import { AppRole, TeamRole } from "./permissionConstants";
import { useBestaetigen } from "../redesign/bestaetigen";

type Team = { id: string; name: string };

type Props = {
  open: boolean;
  teams: Team[];
  onClose: () => void;
  onCreated: () => void;
};

export const UserCreateDialog: React.FC<Props> = ({ open, teams, onClose, onCreated }) => {
  const { hinweisen } = useBestaetigen();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<AppRole>("mitarbeiter");
  const [department, setDepartment] = useState<"FO" | "BO" | "FO+BO">("FO");
  const [teamId, setTeamId] = useState<string>("");
  const [teamRole, setTeamRole] = useState<TeamRole>("teammitglied");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setFirstName("");
      setLastName("");
      setEmail("");
      setUsername("");
      setPassword("");
      setRole("mitarbeiter");
      setDepartment("FO");
      setTeamId("");
      setTeamRole("teammitglied");
    }
  }, [open]);

  const canSubmit = firstName && lastName && email && password;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
      const { data, error } = await authClient.admin.createUser({
        name: fullName || username || email,
        email,
        password,
        role: role as any,
        data: {
          firstName,
          lastName,
          username,
          department,
        } as any,
      });

      if (error) {
        void hinweisen({ titel: "Benutzer nicht angelegt", text: error?.message || "Der Benutzer konnte nicht angelegt werden.", ton: "danger" });
        setSubmitting(false);
        return;
      }

      const newUserId = (data as any)?.user?.id;
      if (newUserId && teamId) {
        const res = await fetch(apiUrl(`rbac/teams/${teamId}/members`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: newUserId, teamRole }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          void hinweisen({
            titel: "Benutzer angelegt – Team fehlt",
            text: err?.message || "Der Benutzer ist angelegt, konnte aber nicht dem Team zugewiesen werden. Bitte die Zuweisung in der Benutzerverwaltung nachholen.",
            ton: "danger",
          });
        }
      }

      onCreated();
      onClose();
    } catch (e: any) {
      console.error(e);
      void hinweisen({ titel: "Benutzer nicht angelegt", text: e?.message || "Beim Anlegen des Benutzers ist ein Fehler aufgetreten.", ton: "danger" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Neuen Benutzer anlegen</DialogTitle>
          <DialogContent style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 16 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <Label htmlFor="cu-firstname" required>Vorname</Label>
                <Input id="cu-firstname" value={firstName} onChange={(_, d) => setFirstName(d.value)} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <Label htmlFor="cu-lastname" required>Nachname</Label>
                <Input id="cu-lastname" value={lastName} onChange={(_, d) => setLastName(d.value)} />
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Label htmlFor="cu-email" required>E-Mail</Label>
              <Input id="cu-email" type="email" value={email} onChange={(_, d) => setEmail(d.value)} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <Label htmlFor="cu-username">Kürzel</Label>
                <Input id="cu-username" value={username} onChange={(_, d) => setUsername(d.value)} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <Label htmlFor="cu-pwd" required>Passwort</Label>
                <Input id="cu-pwd" type="password" value={password} onChange={(_, d) => setPassword(d.value)} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <Label htmlFor="cu-role" required>Rolle</Label>
                <Select id="cu-role" value={role} onChange={(_, d) => setRole(d.value as AppRole)}>
                  <option value="admin">Admin</option>
                  <option value="gfgl">Geschäftsführung/-leitung (gfgl)</option>
                  <option value="mitarbeiter">Mitarbeiter</option>
                  <option value="service">Service</option>
                </Select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <Label htmlFor="cu-dept" required>FO/BO</Label>
                <Select id="cu-dept" value={department} onChange={(_, d) => setDepartment(d.value as "FO" | "BO" | "FO+BO")}>
                  <option value="FO">Front-Office (FO)</option>
                  <option value="BO">Back-Office (BO)</option>
                  <option value="FO+BO">Front- und Back-Office (FO+BO)</option>
                </Select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <Label htmlFor="cu-team">Team zuweisen</Label>
                <Select id="cu-team" value={teamId} onChange={(_, d) => setTeamId(d.value)}>
                  <option value="">— kein Team —</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
                <Label htmlFor="cu-teamrole">Rolle im Team</Label>
                <Select
                  id="cu-teamrole"
                  value={teamRole}
                  disabled={!teamId}
                  onChange={(_, d) => setTeamRole(d.value as TeamRole)}
                >
                  <option value="teammitglied">Teammitglied</option>
                  <option value="teamleiter">Teamleiter</option>
                </Select>
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>Abbrechen</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={!canSubmit || submitting}>
              {submitting ? <Spinner size="tiny" /> : "Erstellen"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UserCreateDialog;
